import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { X } from "lucide-react";
import Dropdown from "../../components/inputFields/DropDown";
import ErrorBanner from "../../components/dashboard/ErrorBanner";
import { OrderFilters, PeriodFilter, NO_RANGE, formatInr, formatNum } from "./ProductSales";
import { getProductSalesCombined, getProductSalesCombinedDistricts } from "../../services/productSales";
import DistrictSalesMap from "../../components/Maps/DistrictSalesMap";
import { getFyOptions, ALL_ORDER_TYPES, PRODUCT_GROUPS, filterParams } from "./productSalesOptions";

const LABEL = "text-[11px] font-semibold uppercase tracking-wider text-gray-500";
const TH = "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-600 bg-gray-100 whitespace-nowrap";
const TD = "px-3 py-2.5 text-gray-700 whitespace-nowrap border-t border-gray-100";
const FY_OPTIONS = getFyOptions();
const CUSTOM = "__custom";

/** Several products added up. URL: /product-sales/combined?skus=A,B,C&group=<name> */
const ProductSalesCombined = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const skus = (searchParams.get("skus") || "").split(",").filter(Boolean);
  const groupName = searchParams.get("group") || "";
  const skusKey = skus.join(",");

  const [fy, setFy] = useState(FY_OPTIONS[0].value);
  const [month, setMonth] = useState("all");
  const [dateRange, setDateRange] = useState(NO_RANGE);
  const [orderTypes, setOrderTypes] = useState(ALL_ORDER_TYPES);
  const [statuses, setStatuses] = useState(["DELIVERED"]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState(null);
  const [districtsLoading, setDistrictsLoading] = useState(false);

  const paramsKey = JSON.stringify({ ...filterParams({ fy, month, dateRange, orderTypes, statuses }), skus: skusKey });

  const load = useCallback(() => {
    if (!skusKey) return;
    setLoading(true);
    setError(false);
    getProductSalesCombined(JSON.parse(paramsKey))
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [paramsKey, skusKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!skusKey) return;
    setDistrictsLoading(true);
    getProductSalesCombinedDistricts(JSON.parse(paramsKey))
      .then((res) => setDistricts(res.data))
      .catch(() => setDistricts(null))
      .finally(() => setDistrictsLoading(false));
  }, [paramsKey, skusKey]);

  const open = (nextSkus, nextGroup) => {
    const q = new URLSearchParams({ skus: nextSkus.join(",") });
    if (nextGroup) q.set("group", nextGroup);
    navigate(`/product-sales/combined?${q.toString()}`);
  };
  // Dropping a product makes it a custom pick
  const remove = (sku) => open(skus.filter((s) => s !== sku), "");

  if (!skusKey) {
    return (
      <div className="p-5 space-y-2">
        <p className="text-sm text-gray-600">No products picked.</p>
        <Link to="/product-sales" className="text-sm font-semibold text-green-700">
          Back to Product Sales
        </Link>
      </div>
    );
  }

  const title = groupName || "Custom pick";
  const products = data?.products || [];
  const rows = data?.months || [];
  const groupOptions = [
    ...PRODUCT_GROUPS.map((g) => ({ value: g.name, label: `${g.name} (${g.skus.length})` })),
    { value: CUSTOM, label: "Custom pick" },
  ];
  const cards = data
    ? [
        { label: "Quantity sold", value: formatNum(data.total_units), sub: data.fy && month !== "all" ? `${month}, ${data.period}` : data.period },
        { label: "Revenue", value: formatInr(data.total_revenue), sub: `${products.length} products added up` },
        { label: "Avg selling price", value: data.total_units ? formatInr(data.avg_price, 2) : "—", sub: "Total revenue ÷ total units" },
        { label: "Orders", value: formatNum(data.total_orders), sub: "Orders with any of these products (counted once)" },
      ]
    : [];

  return (
    <div className="p-3 md:p-5 space-y-4 bg-gray-50 min-h-screen">
      <p className="text-xs text-gray-500">
        <Link to="/product-sales" className="font-medium text-green-700 hover:text-green-800">
          Product Sales
        </Link>{" "}
        / {title} (combined)
      </p>

      {/* Header + filters */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-visible">
        <div className="px-4 py-4 sm:px-5 space-y-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900 tracking-tight">{title}</h1>
              <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
                {skus.length} products combined
              </span>
              {loading && <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-blue-500" title="Loading" />}
            </div>
            <p className="mt-1 text-xs text-gray-500">Every number below adds up the products listed under it.</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <p className={`${LABEL} mb-1`}>Group</p>
              <Dropdown
                options={groupOptions}
                value={groupName && PRODUCT_GROUPS.some((g) => g.name === groupName) ? groupName : CUSTOM}
                onChange={(v) => {
                  const g = PRODUCT_GROUPS.find((x) => x.name === v);
                  if (g) open(g.skus, g.name);
                }}
                compact
              />
            </div>
            <PeriodFilter fy={fy} setFy={setFy} month={month} setMonth={setMonth} dateRange={dateRange} setDateRange={setDateRange} />
          </div>
        </div>
        <OrderFilters orderTypes={orderTypes} setOrderTypes={setOrderTypes} statuses={statuses} setStatuses={setStatuses} />
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-4 py-3 sm:px-5">
          <span className={`${LABEL} mr-1`}>Products in view</span>
          {skus.map((sku) => {
            const p = products.find((x) => x.sku === sku);
            return (
              <span key={sku} className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white py-1 pl-3 pr-1 text-xs font-medium text-gray-700">
                {p ? p.name : sku}
                {skus.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(sku)}
                    aria-label={`Remove ${p ? p.name : sku}`}
                    className="rounded-full bg-gray-100 p-0.5 text-gray-500 hover:bg-gray-200"
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}
          <Link
            to={`/product-sales?pick=${encodeURIComponent(skusKey)}`}
            className="ml-1 text-sm font-semibold text-green-700 hover:text-green-800"
          >
            + Add or change products
          </Link>
        </div>
        {data?.missing_skus?.length > 0 && (
          <p className="border-t border-amber-100 bg-amber-50 px-5 py-2 text-xs text-amber-800">
            Not found, so left out: {data.missing_skus.join(", ")}
          </p>
        )}
      </div>

      {error && <ErrorBanner onRetry={load} />}

      {data && (
        <div className={`space-y-4 transition-opacity ${loading ? "opacity-50" : ""}`}>
          {/* Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map((c) => (
              <div key={c.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-3.5">
                <p className="text-xs font-medium text-gray-500">{c.label}</p>
                <p className="mt-2 text-lg font-bold text-gray-900 leading-tight">{c.value}</p>
                <p className="mt-1 text-xs text-gray-500">{c.sub}</p>
              </div>
            ))}
          </div>

          {/* Products in this group */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Products in this group</h2>
            <p className="mt-0.5 mb-3 text-xs text-gray-500">How much each product adds to the total. Click a name to open its own page.</p>
            <div className="relative overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={`${TH} text-left`}>Product</th>
                    <th className={`${TH} text-right`}>Qty sold</th>
                    <th className={`${TH} text-right`}>Revenue</th>
                    <th className={`${TH} text-right`}>Avg price</th>
                    <th className={`${TH} text-left min-w-[160px]`}>Share of qty</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.sku}>
                      <td className={TD}>
                        <Link to={`/product-sales/${encodeURIComponent(p.sku)}`} className="font-semibold text-green-700 hover:text-green-800">
                          {p.name}
                        </Link>
                        <p className="text-xs text-gray-500">{[p.variant, p.sku].filter(Boolean).join(" · ")}</p>
                      </td>
                      <td className={`${TD} text-right`}>{formatNum(p.units)}</td>
                      <td className={`${TD} text-right`}>{formatInr(p.revenue)}</td>
                      <td className={`${TD} text-right`}>{p.units ? formatInr(p.avg_price, 2) : "—"}</td>
                      <td className={TD}>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 rounded-full bg-gray-100">
                            <div className="h-1.5 rounded-full bg-primary" style={{ width: `${p.share}%` }} />
                          </div>
                          <span className="w-12 text-right text-xs text-gray-600">{p.share}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-bold text-gray-900">
                    <td className={TD}>Total</td>
                    <td className={`${TD} text-right`}>{formatNum(data.total_units)}</td>
                    <td className={`${TD} text-right`}>{formatInr(data.total_revenue)}</td>
                    <td className={`${TD} text-right`}>{data.total_units ? formatInr(data.avg_price, 2) : "—"}</td>
                    <td className={TD}>100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly chart */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Monthly quantity sold</h2>
            <p className="mt-0.5 mb-3 text-xs text-gray-500">All these products together. Hover a bar to see revenue.</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={rows} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
                  formatter={(v, name, item) => [`${formatNum(v)} units · ${formatInr(item.payload.revenue)}`, "Sold"]}
                />
                <Bar dataKey="units" fill="#2FB95D" radius={[4, 4, 0, 0]} maxBarSize={56} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Sales by district */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Sales by district</h2>
            <p className="mt-0.5 mb-2 text-xs text-gray-500">
              Where these products were delivered, added together. Placed by pincode (or the typed district). Same filters as above.
            </p>
            <DistrictSalesMap data={districts} loading={districtsLoading} />
          </div>

          {/* Month by month */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <h2 className="mb-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Month by month</h2>
            <div className="relative overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={`${TH} text-left`}>Month</th>
                    <th className={`${TH} text-right`}>Qty sold</th>
                    <th className={`${TH} text-right`}>Revenue</th>
                    <th className={`${TH} text-right`}>Avg price</th>
                    <th className={`${TH} text-right`}>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.month}-${r.year}`}>
                      <td className={`${TD} font-semibold text-gray-900`}>
                        {r.month} {r.year}
                      </td>
                      <td className={`${TD} text-right`}>{formatNum(r.units)}</td>
                      <td className={`${TD} text-right`}>{formatInr(r.revenue)}</td>
                      <td className={`${TD} text-right`}>{r.units ? formatInr(r.avg_price, 2) : "—"}</td>
                      <td className={`${TD} text-right`}>{formatNum(r.orders)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSalesCombined;
